import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { insertLenderPricingTierSchema, type LenderPricingTier } from "@shared/schema";
import { z } from "zod";

type PricingTierFormData = z.infer<typeof insertLenderPricingTierSchema>;

interface PricingTierFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lenderProductId: string;
  pricingTier?: LenderPricingTier;
  onPricingTierCreated?: (pricingTier: LenderPricingTier) => void;
}

export function PricingTierFormModal({
  open,
  onOpenChange,
  lenderProductId,
  pricingTier,
  onPricingTierCreated,
}: PricingTierFormModalProps) {
  const { toast } = useToast();
  const isEdit = !!pricingTier;

  const defaultValues: Partial<PricingTierFormData> = {
    lenderProductId,
    lvrMin: "",
    lvrMax: "",
    margin: "0.0000",
  };

  // Helper: Convert decimal to percentage for display (0.015 -> 1.5)
  const decimalToPercent = (value: string | null | undefined): string => {
    if (!value || value === "") return "";
    const decimal = parseFloat(value);
    if (isNaN(decimal)) return "";
    return (decimal * 100).toString();
  };

  // Helper: Convert percentage to decimal for storage (1.5 -> 0.015)
  const percentToDecimal = (value: string | null | undefined): string => {
    if (!value || value === "") return "0.0000";
    const percent = parseFloat(value);
    if (isNaN(percent)) return "0.0000";
    return (percent / 100).toFixed(4);
  };

  const form = useForm<PricingTierFormData>({
    resolver: zodResolver(insertLenderPricingTierSchema),
    defaultValues,
    mode: "onChange",
    criteriaMode: "all",
  });

  useEffect(() => {
    if (open) {
      if (pricingTier) {
        // Only reset with form-relevant fields to avoid Zod strict mode validation errors
        // Database-only fields (id, createdAt, updatedAt, organisationId) cause silent validation failures
        form.reset({
          lenderProductId: pricingTier.lenderProductId,
          lvrMin: pricingTier.lvrMin,
          lvrMax: pricingTier.lvrMax,
          margin: decimalToPercent(pricingTier.margin), // Convert for display
        });
      } else {
        form.reset(defaultValues);
      }
    }
  }, [open, pricingTier, form]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset(defaultValues);
    }
    onOpenChange(newOpen);
  };

  const createMutation = useMutation({
    mutationFn: async (data: PricingTierFormData) => {
      const dataWithDecimal = {
        ...data,
        margin: percentToDecimal(data.margin), // Convert to decimal for storage
      };
      const response = await apiRequest("POST", `/api/crm/lender-products/${lenderProductId}/pricing-tiers`, dataWithDecimal);
      return response.json();
    },
    onSuccess: async (newTier: LenderPricingTier) => {
      queryClient.refetchQueries({ queryKey: ["/api/crm/lender-products", lenderProductId, "pricing-tiers"] });
      toast({
        title: "Pricing tier created",
        description: "The pricing tier has been created successfully.",
      });
      onOpenChange(false);
      form.reset(defaultValues);

      if (onPricingTierCreated) {
        onPricingTierCreated(newTier);
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: PricingTierFormData) => {
      const dataWithDecimal = {
        ...data,
        margin: percentToDecimal(data.margin), // Convert to decimal for storage
      };
      return apiRequest("PATCH", `/api/crm/pricing-tiers/${pricingTier?.id}`, dataWithDecimal);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["/api/crm/lender-products", lenderProductId, "pricing-tiers"] });
      toast({
        title: "Pricing tier updated",
        description: "The pricing tier has been updated successfully.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: PricingTierFormData) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Pricing Tier" : "Add Pricing Tier"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update LVR-based pricing tier"
              : "Create a new LVR-based pricing tier"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="lvrMin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LVR Min (%)*</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        data-testid="input-lvr-min"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Minimum LVR for this tier
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lvrMax"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LVR Max (%)*</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        step="0.01"
                        placeholder="80.00"
                        data-testid="input-lvr-max"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Maximum LVR for this tier
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="margin"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Margin *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        step="0.01"
                        placeholder="1.50"
                        data-testid="input-margin"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Enter as percentage (e.g., 1.5 for 1.5%)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Audit Information - Read Only (only visible in edit mode) */}
            {isEdit && pricingTier && (
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-sm font-semibold text-muted-foreground">Audit Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Date Created</label>
                    <div className="mt-1 text-sm bg-muted/50 px-3 py-2 rounded-md" data-testid="display-tier-created-at">
                      {pricingTier.createdAt
                        ? new Date(pricingTier.createdAt).toLocaleString("en-AU", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Date Updated</label>
                    <div className="mt-1 text-sm bg-muted/50 px-3 py-2 rounded-md" data-testid="display-tier-updated-at">
                      {pricingTier.updatedAt
                        ? new Date(pricingTier.updatedAt).toLocaleString("en-AU", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })
                        : "-"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2 mt-6">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isPending}
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isPending}
                  data-testid="button-save"
                >
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isEdit ? "Update" : "Create"} Tier
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
